import { Dimensions } from 'react-native';
import { PlayInstallReferrerInfo } from 'react-native-play-install-referrer';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { updateUserLanguage } from '../api/kisaan/updateUserLanguage';
import { KVPair } from '../interface/keyValuePair';
import { zustandAppStorage } from '../mmkv/mmkvAppPersistence';
import { MMKVStoreKeyMap } from '../mmkv/mmkvStoreKeyMap';
import { SupportedClientAdSizes } from '../utils/adSizes';
import { useProfileStore } from './useProfileStore';
interface AdData {
  adaptive: boolean;
  adUnitsWithSizes: KVPair;
}

const DEFAULT_AD_UNIT_SIZES = [
  '240x133',
  '250x250',
  '250x360',
  '292x30',
  '300x50',
  '300x75',
  '300x100',
  '300x250',
  '320x50',
  '320x100',
  '336x280',
  '350x100',
  '350x200',
  '350x250',
  '400x300',
];

const DEVICE_WIDTH = Dimensions.get('screen').width;

export interface AppSettings {
  appLanguage?: string;
  setAppConfig: (propName: string, propValue: unknown) => void;
  setAppLanguage: (languageKey: string) => void;
  setAppLanguageLabel: (languageLabel: string) => void;
  appLanguageLabel: string;
  deviceId?: string;

  hasActiveTourGuides: { [key: string]: boolean };
  setHasActiveTourGuides: (key: string, isActive: boolean) => void;
  onLoginRedirect: any;
  setOnLoginRedirect: (redirect: any) => void;
  adId: any;
  setAdId: (id: any) => void;
  isAdTrackingLimited: boolean;
  setIsAdTrackingLimited: (isLimited: boolean) => void;
  selectLanguageDetection: boolean;
  setSelectLanguageDetection: (selectLanguageDetection: boolean) => void;
  ads: AdData;
  setAdSizes: (adUnits: KVPair) => void;
  setAdSizeWeightage: (adaptive: boolean) => void;
  activeProductTabLanguages: string[];
  setActiveProductTabLanguages: (enabledLanguages: string[]) => void;
  referInfo?: PlayInstallReferrerInfo;
  setReferrerInfo: (referInfo: PlayInstallReferrerInfo | null) => void;
  firstLaunchTime: number;
  setFirstLaunchTime: (time: number) => void;
  setDeviceId: (uniqueDeviceId: string) => void;
  userName: string;
  setUserName: (name: string) => void;
  isAdsInitialized: boolean;
  setIsAdsInitialized: (status: boolean) => void;
}

//createAppPersistenceStore

export const useAppPersistenceStore = create<AppSettings>()(
  persist(
    (set, get) => ({
      appLanguage: 'hi',
      onLoginRedirect: undefined,
      setAppConfig: (propName, propValue) => set(() => ({ [propName]: propValue })),
      setAppLanguage: (languageKey) => {
        // console.log('set lang => ', languageKey);
        set(() => ({ appLanguage: languageKey }));
        /**
         * TODO: This is a workaround. We are firing and forgetting
         * Lets combine this into a profile api call as well
         */
        // Retrieve necessary values from `useProfileStore`
        const userProfile = useProfileStore.getState().userProfile;
        const userId = userProfile?.user?.id;
        const realmId = userProfile?.realm?.id;
        const accessToken = userProfile?.access_token;

        // Ensure user and realm IDs are available before updating language
        if (userId && realmId && accessToken) {
          updateUserLanguage(
            languageKey,
            userId,
            realmId,
            accessToken,
            useProfileStore.getState().setUserProfile
          ).catch((err) => console.error('Update language error!!!', err));
        } else {
          console.log('User or realm ID is missing, aborting language update.');
        }
      },
      setAppLanguageLabel: (languageLabel) => set(() => ({ appLanguageLabel: languageLabel })),
      appLanguageLabel: get()?.appLanguageLabel ?? 'Hindi',
      hasActiveTourGuides: {
        home: false,
        qna: false,
        feed: false,
        homeHeader: false,
      },
      setHasActiveTourGuides: (key, isActive) => {
        set((state) => ({
          hasActiveTourGuides: {
            ...state.hasActiveTourGuides, // Spread the existing state
            [key]: isActive, // Update only the specific key
          },
        }));
      },
      setOnLoginRedirect: (redirect: any) => set(() => ({ onLoginRedirect: redirect })),
      setAdId: (id: any) => set(() => ({ adId: id })),
      adId: {},
      isAdTrackingLimited: false,
      setIsAdTrackingLimited: (isLimited: boolean) =>
        set(() => ({ isAdTrackingLimited: isLimited })),
      selectLanguageDetection: false,
      setSelectLanguageDetection: (selectLanguageDetection: boolean) =>
        set(() => ({ selectLanguageDetection })),
      ads: {
        adaptive: false,
        adUnitsWithSizes: {},
      },
      setAdSizes: (adUnits: KVPair) => {
        const adKeys = Object.keys(adUnits);
        if (adKeys?.length) {
          const adsState = get()?.ads;
          for (let adKeyIndex = 0; adKeyIndex < adKeys.length; adKeyIndex++) {
            const adKey = adKeys[adKeyIndex];
            const adSizes = adUnits[adKey] ?? DEFAULT_AD_UNIT_SIZES;
            const adWidths = adSizes?.filter((unitSize) => {
              const width = Number(unitSize.split(`x`)?.[0]) ?? 320;
              if (width <= DEVICE_WIDTH) {
                return unitSize;
              }
            });

            const reverseArrWithSizes = adWidths.reverse();
            const prefferedSize = get()?.ads.adaptive ? reverseArrWithSizes.shift() : undefined;
            const hasPrefferedSize = prefferedSize ? [prefferedSize] : [];
            adsState.adUnitsWithSizes[adKey] = Array.from(
              new Set([...hasPrefferedSize, ...SupportedClientAdSizes, ...reverseArrWithSizes])
            );
          }
          set(() => ({ ads: adsState }));
        }
      },
      setAdSizeWeightage: (adaptive: boolean) => {
        const adState = get()?.ads;
        adState.adaptive = adaptive;
        set(() => ({ ads: adState }));
      },
      activeProductTabLanguages: ['hi', 'en'],
      setActiveProductTabLanguages: (activeLanguages) =>
        set(() => ({ activeProductTabLanguages: activeLanguages })),
      setReferrerInfo: (adReferInfo: PlayInstallReferrerInfo) =>
        set(() => ({ referInfo: adReferInfo })),
      referInfo: undefined,
      firstLaunchTime: 0,
      setFirstLaunchTime: (time: number) => set(() => ({ firstLaunchTime: time })),
      setDeviceId: async (uniqueDeviceId) => {
        set(() => ({ deviceId: uniqueDeviceId }));
      },
      userName: '',
      setUserName: (name: string) => set(() => ({ userName: name })),
      isAdsInitialized: false,
      setIsAdsInitialized: (isAdsInitialized) => set(() => ({ isAdsInitialized })),
    }),

    {
      name: MMKVStoreKeyMap.appSettings,
      storage: createJSONStorage(() => zustandAppStorage),
    }
  )
);

// export const useAppPersistenceStore = () => {
//   return createAppPersistenceStore<AppSettings>((store) => ({
//     appLanguage: store.appLanguage,
//     setAppConfig: store.setAppConfig,
//     setAppLanguage: store.setAppLanguage,
//     appLanguageLabel: store.appLanguageLabel,
//     setAppLanguageLabel: store.setAppLanguageLabel,
//     eventDetails: store.eventDetails,
//     setEventDetails: store.setEventDetails,
//     hasActiveTourGuides: store.hasActiveTourGuides,
//     setHasActiveTourGuides: store.setHasActiveTourGuides
//   }));
// };
